import { clamp } from '@/domain/reporting/reporting-shared.math';
import { type TreemapLayoutInputItem, type TreemapLayoutItem } from '@/domain/reporting/reporting-shared.types';

const sumMetric = <T extends { value: number }>(rows: T[]): number =>
    rows.reduce((acc, row) => acc + Math.max(0, row.value || 0), 0);

export const buildTreemapLayout = (
    items: TreemapLayoutInputItem[],
    options: {
        maxItems?: number;
    } = {},
): TreemapLayoutItem[] => {
    const maxItems =
        typeof options.maxItems === 'number' && Number.isFinite(options.maxItems) && options.maxItems > 0
            ? Math.floor(options.maxItems)
            : null;

    const rows = items
        .filter((item) => item.value > 0)
        .sort((leftItem, rightItem) => rightItem.value - leftItem.value)
        .slice(0, maxItems === null ? undefined : maxItems)
        .map((item) => ({ ...item }));

    const totalWeight = sumMetric(rows);
    if (!rows.length || totalWeight <= 0) {
        return [];
    }

    const layout: TreemapLayoutItem[] = [];

    const place = (
        currentItems: TreemapLayoutInputItem[],
        x: number,
        y: number,
        width: number,
        height: number,
    ): void => {
        if (!currentItems.length || width <= 0 || height <= 0) {
            return;
        }

        if (currentItems.length === 1) {
            layout.push({
                ...currentItems[0],
                x,
                y,
                width,
                height,
            });

            return;
        }

        const currentWeight = sumMetric(currentItems);
        if (currentWeight <= 0) {
            return;
        }

        let splitIndex = 1;
        let leftWeight = currentItems[0].value;
        let closestDelta = Math.abs(currentWeight / 2 - leftWeight);

        for (let index = 1; index < currentItems.length - 1; index += 1) {
            leftWeight += currentItems[index].value;
            const delta = Math.abs(currentWeight / 2 - leftWeight);

            if (delta > closestDelta) {
                leftWeight -= currentItems[index].value;
                break;
            }

            closestDelta = delta;
            splitIndex = index + 1;
        }

        splitIndex = clamp(splitIndex, 1, currentItems.length - 1);

        const leftItems = currentItems.slice(0, splitIndex);
        const rightItems = currentItems.slice(splitIndex);
        const leftItemsWeight = sumMetric(leftItems);

        if (!rightItems.length || leftItemsWeight <= 0) {
            place(currentItems.slice(0, currentItems.length - 1), x, y, width, height);
            place(
                [currentItems[currentItems.length - 1]],
                x + width * 0.8,
                y + height * 0.8,
                width * 0.2,
                height * 0.2,
            );
            return;
        }

        const leftPartRatio = leftItemsWeight / currentWeight;

        if (width >= height) {
            const leftWidth = width * leftPartRatio;

            place(leftItems, x, y, leftWidth, height);
            place(rightItems, x + leftWidth, y, width - leftWidth, height);
            return;
        }

        const topHeight = height * leftPartRatio;
        place(leftItems, x, y, width, topHeight);
        place(rightItems, x, y + topHeight, width, height - topHeight);
    };

    place(rows, 0, 0, 100, 100);

    return layout;
};
